import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useId } from "react";
import styled from "styled-components";
import { spacing, textStyle } from "../../dimensions";
import { ChevronIcon } from "../../icons";
import { AccordionProvider, useAccordionContext } from "./AccordionContext";
import { buttonreset } from "../../utils/snippets";
import { AccordionItemProvider, useAccordionItemContext } from "./AccordionItemContext";
var SUPPORTED_KEYS = ["ArrowUp", "ArrowDown", "Home", "End"];
var Accordion = function Accordion(_ref) {
  var _ref$type = _ref.type,
    type = _ref$type === void 0 ? "single" : _ref$type,
    children = _ref.children,
    _ref$showBottomBorder = _ref.showBottomBorder,
    showBottomBorder = _ref$showBottomBorder === void 0 ? true : _ref$showBottomBorder,
    _ref$showTopBorder = _ref.showTopBorder,
    showTopBorder = _ref$showTopBorder === void 0 ? true : _ref$showTopBorder;
  var baseId = useId();
  var defaultOpenIds = [];
  var childrenWithIds = React.Children.map(children, function (child, index) {
    if (/*#__PURE__*/React.isValidElement(child) && child.type === AccordionItem) {
      var _child$props$id;
      var id = (_child$props$id = child.props.id) !== null && _child$props$id !== void 0 ? _child$props$id : "".concat(baseId, "-").concat(index);
      if (child.props.defaultOpen) {
        defaultOpenIds.push(id);
      }
      return /*#__PURE__*/React.cloneElement(child, _objectSpread(_objectSpread({}, child.props), {}, {
        id: id
      }));
    }
    return child;
  });
  return /*#__PURE__*/React.createElement(AccordionProvider, {
    type: type,
    defaultOpenIds: defaultOpenIds
  }, /*#__PURE__*/React.createElement(AccordionContainer, {
    showTopBorder: showTopBorder,
    showBottomBorder: showBottomBorder
  }, childrenWithIds));
};
var AccordionContainer = function AccordionContainer(_ref2) {
  var children = _ref2.children,
    showTopBorder = _ref2.showTopBorder,
    showBottomBorder = _ref2.showBottomBorder;
  var handleKeydown = function handleKeydown(event) {
    var _enabledButtons$nextI;
    if (!SUPPORTED_KEYS.includes(event.key)) {
      return;
    }
    event.preventDefault();
    var enabledButtons = Array.from(event.currentTarget.querySelectorAll("[data-accordion-button]"));
    var activeElement = document.activeElement;
    var currentIndex = enabledButtons.findIndex(function (summary) {
      return summary === activeElement;
    });
    if (currentIndex === -1) return;
    var nextIndex = currentIndex;
    switch (event.key) {
      case "ArrowUp":
        nextIndex = (currentIndex - 1 + enabledButtons.length) % enabledButtons.length;
        break;
      case "ArrowDown":
        nextIndex = (currentIndex + 1) % enabledButtons.length;
        break;
      case "Home":
        nextIndex = 0;
        break;
      case "End":
        nextIndex = enabledButtons.length - 1;
        break;
      default:
        return;
    }
    (_enabledButtons$nextI = enabledButtons[nextIndex]) === null || _enabledButtons$nextI === void 0 || _enabledButtons$nextI.focus();
  };
  return /*#__PURE__*/React.createElement(StyledAccordionContainer, {
    onKeyDown: handleKeydown,
    $showTopBorder: showTopBorder,
    $showBottomBorder: showBottomBorder
  }, children);
};
var AccordionItem = function AccordionItem(_ref3) {
  var children = _ref3.children,
    id = _ref3.id;
  return /*#__PURE__*/React.createElement(AccordionItemProvider, {
    id: id
  }, /*#__PURE__*/React.createElement(StyledAccordionItem, null, children));
};
var AccordionHeader = function AccordionHeader(_ref4) {
  var leadingIcon = _ref4.leadingIcon,
    children = _ref4.children,
    _ref4$maxContentWidth = _ref4.maxContentWidth,
    maxContentWidth = _ref4$maxContentWidth === void 0 ? "80ch" : _ref4$maxContentWidth;
  var _useAccordionContext = useAccordionContext(),
    toggle = _useAccordionContext.toggle;
  var _useAccordionItemCont = useAccordionItemContext(),
    isOpen = _useAccordionItemCont.isOpen,
    id = _useAccordionItemCont.id;
  return /*#__PURE__*/React.createElement(Button, {
    onClick: function onClick() {
      return toggle(id);
    },
    "aria-expanded": isOpen,
    "aria-controls": "panel-".concat(id),
    type: "button",
    "data-accordion-button": true
  }, leadingIcon, /*#__PURE__*/React.createElement(HeaderContentWrapper, {
    $maxContentWidth: maxContentWidth
  }, children), /*#__PURE__*/React.createElement(StyledChevronIcon, null));
};
var AccordionPanel = function AccordionPanel(_ref5) {
  var children = _ref5.children,
    _ref5$maxContentWidth = _ref5.maxContentWidth,
    maxContentWidth = _ref5$maxContentWidth === void 0 ? "80ch" : _ref5$maxContentWidth;
  var _useAccordionItemCont2 = useAccordionItemContext(),
    isOpen = _useAccordionItemCont2.isOpen,
    id = _useAccordionItemCont2.id;
  return /*#__PURE__*/React.createElement(PanelContentWrapper, {
    role: "region",
    id: "panel-".concat(id),
    "aria-labelledby": id,
    $maxContentWidth: maxContentWidth,
    "data-expanded": isOpen ? "" : undefined,
    inert: !isOpen ? "" : undefined
  }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(PanelContent, null, children)));
};
var StyledAccordionContainer = styled.div.withConfig({
  displayName: "Accordion__StyledAccordionContainer",
  componentId: "sc-5lhdj0-0"
})(["--gds-accordion-easing-fn:cubic-bezier(0.3,0,0.2,1);--gds-accordion-transition-duration:300ms;--gds-accordion-inline-padding:", ";--gds-accordion-block-padding:", ";--gds-accordion-border:1px solid ", ";background-color:", ";> * + *{border-top:var(--gds-accordion-border);}", " ", ""], spacing.amphitheatre, spacing.auditorium, function (props) {
  return props.theme.base.borderLight;
}, function (props) {
  return props.theme.base.bg;
}, function (props) {
  return props.$showTopBorder && "border-top: var(--gds-accordion-border);";
}, function (props) {
  return props.$showBottomBorder && "border-bottom: var(--gds-accordion-border);";
});
var StyledAccordionItem = styled.div.withConfig({
  displayName: "Accordion__StyledAccordionItem",
  componentId: "sc-5lhdj0-1"
})([""]);
var Button = styled.button.withConfig({
  displayName: "Accordion__Button",
  componentId: "sc-5lhdj0-2"
})(["", " ", " display:flex;gap:", ";width:100%;padding:var(--gds-accordion-block-padding) var(--gds-accordion-inline-padding);cursor:pointer;transition:all 70ms var(--gds-accordion-easing-fn);&[aria-expanded=\"true\"]{", " background-color:", ";}&:hover,&:not([aria-expanded=\"true\"]):focus-visible{background-color:", ";}"], buttonreset, textStyle.rainier, spacing.hall, textStyle.boising, function (props) {
  return props.theme.base.borderLight;
}, function (props) {
  return props.theme.base.bgAlt;
});
var HeaderContentWrapper = styled.span.withConfig({
  displayName: "Accordion__HeaderContentWrapper",
  componentId: "sc-5lhdj0-3"
})(["flex:1;max-width:", ";"], function (props) {
  return props.$maxContentWidth;
});
var StyledChevronIcon = styled(ChevronIcon).withConfig({
  displayName: "Accordion__StyledChevronIcon",
  componentId: "sc-5lhdj0-4"
})(["margin-left:auto;transition:transform var(--gds-accordion-transition-duration) var(--gds-accordion-easing-fn);", "[aria-expanded=\"true\"] &{transform:rotate(-180deg);}"], Button);
var PanelContentWrapper = styled.div.withConfig({
  displayName: "Accordion__PanelContentWrapper",
  componentId: "sc-5lhdj0-5"
})(["display:grid;grid-template-rows:0fr;max-width:", ";transition:grid-template-rows var(--gds-accordion-transition-duration) var(--gds-accordion-easing-fn);& > *{padding:0;overflow:hidden;}&[data-expanded]{grid-template-rows:1fr;}"], function (props) {
  return props.$maxContentWidth;
});
var PanelContent = styled.div.withConfig({
  displayName: "Accordion__PanelContent",
  componentId: "sc-5lhdj0-6"
})(["padding:var(--gds-accordion-block-padding) var(--gds-accordion-inline-padding);"]);
Accordion.Item = AccordionItem;
Accordion.Header = AccordionHeader;
Accordion.Panel = AccordionPanel;
export default Accordion;